import { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardBody, CardHeader, Modal, Row, Col, Spinner, Input, Badge } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../helpers/api';
import FeatherIcon from 'feather-icons-react';
import * as XLSX from 'xlsx';
import FileModal from '../components/Common/importExportModal';
import ToolsModal from '../components/ToolsModal';
import ToolCheckoutModal from '../components/ToolCheckoutModal';
import ToolCheckinModal from '../components/ToolCheckinModal';
import TicketDetailsModal from '../components/TicketDetailsModal';
import LocationModal from '../components/LocationModal';
import { handleApiResponse, createNotificationFromApiResponse, formatDateForAPI, currencies } from '../constants/utils';
import { getPresignedUrl, validateFiles, uploadDocument } from '../helpers/fileUpload';
import PermissionGuard from '../components/Common/PermissionGuard';
import usePermission from '../Hooks/usePermission';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { useTranslation } from 'react-i18next';

const Tools = () => {
    const { t } = useTranslation();
    document.title = `${t("shared.tools", "Tools")} | Arcmedix`;
    const { isAdmin } = usePermission();
    const [tools, setTools] = useState([]);
    const [modal_large, setmodal_large] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState('');
    const [notification, setNotification] = useState({ type: '', message: '' });
    const [deleteModal, setDeleteModal] = useState(false);
    const [fileModal, setFileModal] = useState(false);
    const [detailsModal, setDetailsModal] = useState(false);
    const [selectedTool, setSelectedTool] = useState(null);
    const [locations, setLocations] = useState([]);
    const [conditions, setConditions] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [docUploading, setDocUploading] = useState(false);

    // Location details modal state
    const [locationDetailsModal, setLocationDetailsModal] = useState({
        isOpen: false, type: null, title: '', mainData: null,
        relatedData: [], loading: false, locationData: null
    });

    // Checkout/Checkin modal states
    const [checkoutModal, setCheckoutModal] = useState(false);
    const [checkinModal, setCheckinModal] = useState(false);
    const [checkoutToolId, setCheckoutToolId] = useState(null);
    const [checkinCheckoutId, setCheckinCheckoutId] = useState(null);

    // Work order details modal from checkout history
    const [woTicketModal, setWoTicketModal] = useState({ isOpen: false, ticketNumber: null });

    const currentUser = JSON.parse(localStorage.getItem('authUser'));
    // Build a lightweight org object from the active context so this page
    // mirrors whichever org the user has currently switched into.
    const organizationId = currentUser?.activeOrganizationId ?? null;
    const _activeOrgName = currentUser?.activeContextName
      || (currentUser?.memberships || []).find((m) => m.organizationId === organizationId)?.organizationName
      || null;
    const organization = organizationId ? { id: organizationId, name: _activeOrgName } : null;

    const toolCategories = [
        { value: 'HAND_TOOLS', label: t('business.tools.cat_hand_tools', 'Hand Tools') },
        { value: 'POWER_TOOLS', label: t('business.tools.cat_power_tools', 'Power Tools') },
        { value: 'MEASURING_INSTRUMENTS', label: t('business.tools.cat_measuring', 'Measuring Instruments') },
        { value: 'SAFETY_EQUIPMENT', label: t('business.tools.cat_safety', 'Safety Equipment') },
        { value: 'LIFTING_RIGGING', label: t('business.tools.cat_lifting', 'Lifting & Rigging') },
        { value: 'WELDING', label: t('business.tools.cat_welding', 'Welding') },
        { value: 'PNEUMATIC_HYDRAULIC', label: t('business.tools.cat_pneumatic', 'Pneumatic / Hydraulic') },
        { value: 'DIAGNOSTIC', label: t('business.tools.cat_diagnostic', 'Diagnostic') },
        { value: 'GENERAL', label: t('business.tools.cat_general', 'General') }
    ];

    const toolStatuses = [
        { value: 'AVAILABLE', label: t('business.tools.status_available', 'Available'), color: 'success' },
        { value: 'UNDER_MAINTENANCE', label: t('business.tools.status_under_maintenance', 'Under Maintenance'), color: 'info' },
        { value: 'OUT_OF_SERVICE', label: t('business.tools.status_out_of_service', 'Out of Service'), color: 'danger' }
    ];

    const statusBadgeMap = {
        'AVAILABLE': { label: t('business.tools.status_available', 'Available'), color: 'success' },
        'CHECKED_OUT': { label: t('business.tools.status_checked_out', 'Checked Out'), color: 'warning' },
        'UNDER_MAINTENANCE': { label: t('business.tools.status_under_maintenance', 'Under Maintenance'), color: 'info' },
        'OUT_OF_SERVICE': { label: t('business.tools.status_out_of_service', 'Out of Service'), color: 'danger' }
    };

    const selectStyles = (hasError) => ({
        control: (base, state) => ({
            ...base,
            borderColor: hasError ? '#dc3545' : (state.isFocused ? '#86b7fe' : '#ced4da'),
            boxShadow: hasError
                ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
                : (state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none'),
            '&:hover': { borderColor: '#86b7fe' }
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#3980c0' : state.isFocused ? '#3980c0' : 'white',
            color: state.isSelected || state.isFocused ? 'white' : '#212529',
            '&:active': { backgroundColor: '#2e669a' }
        })
    });

    const canDeleteTool = (tool) => {
        if (isAdmin) return true;
        const createdById = tool?.createdBy?.id ?? tool?.createdBy;
        const userId = currentUser?.id;
        if (createdById === undefined || createdById === null) return true;
        return createdById === userId;
    };

    const [count, setCount] = useState(10);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterModal, setFilterModal] = useState(false);

    // Flatten locations with path for react-select
    const flattenLocationsWithPath = (locs, parentPath = '') => {
        const result = [];
        locs?.forEach(loc => {
            const path = parentPath ? `${parentPath} → ${loc.locationName}` : loc.locationName;
            result.push({ id: loc.id, name: loc.locationName, path });
            if (loc.subLocations?.length > 0) {
                result.push(...flattenLocationsWithPath(loc.subLocations, path));
            }
        });
        return result;
    };

    const flatLocations = useMemo(() => flattenLocationsWithPath(locations), [locations]);

    const fetchTools = async (filterStatus) => {
        try {
            setLoading(true);
            const status = filterStatus !== undefined ? filterStatus : statusFilter;
            let url = `tool/all?organizationId=${organization?.id || 1}&count=${count}`;
            if (status) url += `&status=${status}`;
            const {data} = await api.get(url);
            setTools(data?.response);
            setHasMore(data?.response?.length === count || data?.length === count);
        } catch (error) {
            console.error('Error fetching tools:', error);
            setApiError(t("business.tools.failed_fetch_data", 'Failed to fetch tools data'));
            setTools([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchLocations = async () => {
        try {
            const { data } = await api.get(`organization-location/get-by-organization-id?organizationId=${organization?.id}&count=9999&hierarchy=true`);
            setLocations(data.response[0]?.locations || []);
        } catch (error) {
            console.error('Error fetching locations:', error);
            setLocations([]);
        }
    };

    const fetchVendors = async () => {
        try {
            const { data } = await api.get(`vendor/getAllVendors`);
            setVendors(data.response || []);
        } catch (error) {
            console.error("Error fetching vendors:", error);
            setVendors([]);
        }
    };

    const fetchConditions = async () => {
        try {
            const { data } = await api.get("dynamic-enum/find-by-name?name=condition");
            setConditions(data.response.values || []);
        } catch (error) {
            console.error('Error fetching conditions:', error);
            setConditions([]);
        }
    };

    const handleSearch = useCallback(async (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setCount(10);

        try {
            setLoading(true);
            const searchParam = term.trim() ? `&search=${encodeURIComponent(term)}` : '';
            const statusParam = statusFilter ? `&status=${statusFilter}` : '';
            const {data} = await api.get(`tool/all?organizationId=${organization?.id || 1}&count=10${searchParam}${statusParam}`);
            setTools(data?.response || data || []);
            setHasMore((data?.response?.length || data?.length || 0) === 10);
        } catch (error) {
            console.error('Error searching tools:', error);
            setApiError(t("business.tools.failed_search", 'Failed to search tools'));
            setTools([]);
        } finally {
            setLoading(false);
        }
    }, [organization?.id, statusFilter]);

    const fetchToolsWithParams = async (status, search) => {
        try {
            setLoading(true);
            let url = `tool/all?organizationId=${organization?.id || 1}&count=10`;
            if (status) url += `&status=${status}`;
            if (search?.trim()) url += `&search=${encodeURIComponent(search)}`;
            const {data} = await api.get(url);
            setTools(data?.response || data || []);
            setHasMore((data?.response?.length || data?.length || 0) === 10);
        } catch (error) {
            console.error('Error fetching tools:', error);
            setApiError(t("business.tools.failed_fetch_data", 'Failed to fetch tools data'));
            setTools([]);
        } finally {
            setLoading(false);
        }
    };

    const handleLoadMore = async () => {
        if (!hasMore || loading) return;

        try {
            setLoading(true);
            const newCount = count + 10;
            const searchParam = searchTerm.trim() ? `&search=${encodeURIComponent(searchTerm)}` : '';
            const statusParam = statusFilter ? `&status=${statusFilter}` : '';
            const {data} = await api.get(`tool/all?organizationId=${organization?.id || 1}&count=${newCount}${searchParam}${statusParam}`);
            setTools(data?.response || data || []);
            setCount(newCount);
            setHasMore((data?.response?.length || data?.length || 0) === newCount);
        } catch (error) {
            console.error('Error loading more tools:', error);
            setApiError(t("business.tools.failed_load_more", 'Failed to load more tools'));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFileModal(false);
        setApiError('');
    };

    const handleImport = async (file) => {
        try {
            setLoading(true);
            setApiError('');

            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            setFileModal(false);
            return { success: true, count: jsonData.length };
        } catch (error) {
            const errorMsg = error.message || t("shared.error_processing_file", "Error processing the file");
            setApiError(errorMsg);
            throw new Error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const formatDateDisplay = (date) => {
        if (!date) return '';
        if (Array.isArray(date)) {
            const [y, m, d] = date;
            return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
        }
        try {
            const d = new Date(date);
            if (isNaN(d.getTime())) return date;
            return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
        } catch {
            return date;
        }
    };

    const handleExport = () => {
        if (!tools || tools.length === 0) return;

        const dataToExport = tools.map((tool, index) => ({
            '#': index + 1,
            [t('shared.tool_name', 'Tool Name')]: tool?.name || '',
            [t('business.tools.tool_id', 'Tool ID')]: tool?.toolNumber || '',
            [t('shared.serial_number', 'Serial Number')]: tool?.serialNumber || '',
            [t('shared.location', 'Location')]: tool?.location || '',
            [t('shared.condition', 'Condition')]: tool?.condition || '',
            [t('shared.status', 'Status')]: tool?.status || '',
            [t('business.tools.category', 'Category')]: tool?.category || '',
            [t('business.tools.manufacturer', 'Manufacturer')]: tool?.manufacturer || '',
            [t('business.tools.model', 'Model')]: tool?.model || '',
            [t('shared.cost', 'Cost')]: tool?.cost || 0,
            [t('business.tools.purchase_date', 'Purchase Date')]: formatDateDisplay(tool?.purchaseDate),
            [t('shared.description', 'Description')]: tool?.description || ''
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Tools');

        XLSX.writeFile(wb, `tools_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const tog_large = (entry = null) => {
        setmodal_large(!modal_large);

        if (entry) {
            // Find locationId from flatLocations
            const matchedLocation = flatLocations.find(loc => loc.name === entry.location || loc.path === entry.location);
            const locationId = entry.locationId || matchedLocation?.id || '';

            setSelectedEntry({
                ...entry,
                locationId,
                description: entry.description || '',
                imageUrl: entry.imageUrl || '',
                category: entry.category || '',
                manufacturer: entry.manufacturer || '',
                model: entry.model || '',
                status: entry.status || 'AVAILABLE',
                purchaseDate: entry.purchaseDate || '',
                currency: entry.currency || 'USD',
                vendorId: entry.vendorId || entry.vendor?.id || '',
                toolNumber: entry.toolNumber || '',
                documentUrls: entry.documentUrls || []
            });
        } else {
            setSelectedEntry({
                name: '',
                serialNumber: '',
                location: '',
                condition: '',
                cost: 0,
                currency: 'USD',
                locationId: '',
                organization: organization,
                description: '',
                imageUrl: '',
                category: '',
                manufacturer: '',
                model: '',
                status: 'AVAILABLE',
                purchaseDate: '',
                vendorId: '',
                toolNumber: '',
                documentUrls: []
            });
        }

        setErrors({});
        setApiError('');
    };

    const handleModalClose = () => {
        setmodal_large(false);
        setSelectedEntry(null);
        setErrors({});
        setApiError('');
        setNotification({ type: '', message: '' });
    };

    const handleUpdate = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!selectedEntry?.name?.trim()) newErrors.name = t("shared.tool_name_required", 'Tool name is required');
        if (!selectedEntry?.locationId) newErrors.location = t("shared.location_required", 'Location is required');
        if (!selectedEntry?.condition?.trim()) newErrors.condition = t("shared.condition_required", 'Condition is required');

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            setApiError('');
            setNotification({ type: '', message: '' });
            setLoading(true);

            const toolData = {
                name: selectedEntry.name,
                serialNumber: selectedEntry.serialNumber || null,
                locationId: selectedEntry.locationId,
                condition: selectedEntry.condition,
                cost: selectedEntry.cost || 0,
                currency: selectedEntry.currency || 'USD',
                organizationId: organization?.id,
                description: selectedEntry.description || null,
                imageUrl: selectedEntry.imageUrl || null,
                category: selectedEntry.category || null,
                manufacturer: selectedEntry.manufacturer || null,
                model: selectedEntry.model || null,
                status: selectedEntry.status || 'AVAILABLE',
                purchaseDate: selectedEntry.purchaseDate ? `${String(selectedEntry.purchaseDate).split('T')[0]}T00:00:00` : null,
                vendorId: selectedEntry.vendorId || null,
                toolNumber: selectedEntry.toolNumber || null,
                documentUrls: selectedEntry.documentUrls || []
            };

            let response;
            if (selectedEntry?.id) {
                response = await api.put(`tool/update`, [{ ...toolData, id: selectedEntry.id }]);
            } else {
                response = await api.post('tool/create', [toolData]);
            }

            const result = handleApiResponse(
                response.data,
                (data) => {},
                (warnings, data) => {
                    console.warn('Tool operation completed with warnings:', warnings);
                },
                (error) => {
                    console.error('Tool operation failed:', error);
                }
            );

            const notif = createNotificationFromApiResponse(
                result,
                selectedEntry?.id ? t("business.tools.updated_successfully", 'Tool updated successfully') : t("business.tools.created_successfully", 'Tool created successfully'),
                t("business.tools.some_tools_failed", 'Some tools could not be processed')
            );

            setNotification(notif);

            await fetchTools();

            if (result.success && !result.hasWarnings) {
                handleModalClose();
            }
        } catch (error) {
            setApiError(error?.response?.data?.error || error?.response?.data?.message || t("shared.something_went_wrong", "Something went wrong!"));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setApiError('');
            setLoading(true);

            await api.delete(`tool/delete?id=${selectedEntry?.id}`);

            await fetchTools();
            setDeleteModal(false);
        } catch (error) {
            console.error('Error deleting tool:', error);
            setApiError(error?.response?.data?.error || error?.response?.data?.message || t("business.tools.failed_delete", 'Failed to delete tool'));
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const publicURL = await getPresignedUrl(file);
            setSelectedEntry(prev => ({
                ...prev,
                imageUrl: publicURL
            }));
        } catch (err) {
            setApiError(t("shared.error_during_upload", "Error during upload: ") + err.message);
        }
    };

    const handleDocumentChange = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const validation = validateFiles(Array.from(files));
        if (validation.errors.length > 0) {
            setApiError(validation.errors.join('\n'));
        }

        if (validation.validFiles.length === 0) {
            e.target.value = '';
            return;
        }

        setDocUploading(true);
        try {
            const uploadedUrls = [];

            for (let i = 0; i < validation.validFiles.length; i++) {
                const file = validation.validFiles[i];
                const publicURL = await uploadDocument(file);
                if (publicURL) {
                    uploadedUrls.push(publicURL);
                }
            }

            setSelectedEntry(prev => ({
                ...prev,
                documentUrls: [...(prev.documentUrls || []), ...uploadedUrls]
            }));

            e.target.value = '';
        } catch (err) {
            setApiError(t("shared.error_during_upload", "Error during upload: ") + err.message);
        } finally {
            setDocUploading(false);
        }
    };

    const handleDeleteDocument = (index) => {
        const updatedDocuments = selectedEntry?.documentUrls?.filter((_, i) => i !== index) || [];
        setSelectedEntry(prev => ({
            ...prev,
            documentUrls: updatedDocuments
        }));
    };

    const getDocumentIcon = (url) => {
        const ext = url?.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'pdf':
                return 'file-text';
            case 'doc':
            case 'docx':
                return 'file';
            case 'xls':
            case 'xlsx':
                return 'grid';
            default:
                return 'file';
        }
    };

    const getFileNameFromUrl = (url) => {
        try {
            const pathname = new URL(url).pathname;
            return decodeURIComponent(pathname.split('/').pop() || 'Document');
        } catch {
            return url?.split('/').pop() || 'Document';
        }
    };

    // Checkout/Checkin handlers
    const handleCheckoutFromModal = (tool) => {
        setCheckoutToolId(tool.id);
        setDetailsModal(false);
        setCheckoutModal(true);
    };

    const handleCheckinFromModal = (checkoutId) => {
        setCheckinCheckoutId(checkoutId);
        setDetailsModal(false);
        setCheckinModal(true);
    };

    const handleCheckinFromList = async (toolId) => {
        try {
            const { data } = await api.get(`work-order-tool/history?toolId=${toolId}&count=5`);
            const history = data?.response || [];
            const active = history.find(h => h.status === 'CHECKED_OUT');
            if (active?.id) {
                setCheckinCheckoutId(active.id);
                setCheckinModal(true);
            }
        } catch (err) {
            console.error('Error fetching active checkout:', err);
        }
    };

    const handleCheckoutSuccess = async () => {
        setCheckoutModal(false);
        setCheckoutToolId(null);
        await fetchTools();
    };

    const handleCheckinSuccess = async () => {
        setCheckinModal(false);
        setCheckinCheckoutId(null);
        await fetchTools();
    };

    useEffect(() => {
        fetchLocations();
        fetchConditions();
        fetchVendors();
        fetchTools();
    }, []);

    useEffect(() => {
        if (organization?.id) {
            fetchTools();
        }
    }, [count]);

    const getStatusBadge = (status) => {
        const config = statusBadgeMap[status];
        if (!config) return null;
        return <Badge color={config.color}>{config.label}</Badge>;
    };

    const getCategoryLabel = (category) => {
        const cat = toolCategories.find(c => c.value === category);
        return cat ? cat.label : (category ? category.replace(/_/g, ' ') : '');
    };

    const handleLocationClick = async (locationName) => {
        let locationData = null;
        const foundLocation = locations?.find(loc => {
            if (loc.locationName === locationName) return true;
            return loc.subLocations?.some(sub => sub.locationName === locationName);
        });
        if (foundLocation) {
            if (foundLocation.locationName === locationName) {
                locationData = { ...foundLocation, organization };
            } else {
                const sublocation = foundLocation.subLocations.find(sub => sub.locationName === locationName);
                locationData = { ...sublocation, organization };
            }
        }
        setLocationDetailsModal({
            isOpen: true, type: 'location',
            title: t("shared.location_details", 'Location Details'),
            mainData: locationName, relatedData: [], loading: true,
            locationData
        });
        try {
            const { data } = await api.get('asset-issues/organization/filtered', {
                params: { organizationLocation: locationName }
            });
            setLocationDetailsModal(prev => ({ ...prev, relatedData: data.response || [], loading: false }));
        } catch (error) {
            console.error('Error fetching location details:', error);
            setLocationDetailsModal(prev => ({ ...prev, loading: false }));
        }
    };

    return (
        <div className='page-content mt-4'>
            <div className="mb-4 d-flex align-items-center gap-3">
                <motion.div
                    className="mb-4 d-flex align-items-center gap-3"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <PermissionGuard permission="tool:create">
                        <button
                            className="btn btn-success"
                            onClick={() => tog_large()}
                            title={t("business.tools.add_tool", "Add Tool")}
                        >
                            <FeatherIcon icon="plus" size={18} className="me-1" />
                            {t("business.tools.add_tool", "Add Tool")}
                        </button>
                    </PermissionGuard>

                    <div className="ms-auto" style={{ minWidth: '400px', flexGrow: 1, maxWidth: '500px' }}>
                        <Input
                            type="text"
                            placeholder={t("business.tools.search_placeholder", "Search Tools...")}
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                </motion.div>
            </div>

            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className="card-title text-success mb-0">{t("shared.tools", "Tools")}</h3>
                        <div className="d-flex align-items-center gap-2">
                            <button
                                className="btn btn-soft-primary p-2"
                                onClick={() => setFilterModal(true)}
                                title={t("business.tools.filter_tools", "Filter tools")}
                            >
                                <FeatherIcon icon="filter" size={18} />
                            </button>
                            <button
                                className="btn btn-soft-info p-2"
                                onClick={() => setFileModal(true)}
                                title={t("business.tools.import_export", "Import/Export tools")}
                            >
                                <FeatherIcon icon="file-text" size={18} />
                            </button>
                        </div>
                    </div>
                </CardHeader>

                <CardBody>
                    <div
                        className="table-responsive"
                        style={{
                            overflowX: 'auto',
                            whiteSpace: 'nowrap',
                            maxHeight: "calc(100vh - 300px)",
                            overflowY: "auto",
                        }}
                    >
                        <table className="table table-hover mb-0" style={{
                            borderSpacing: '0.1rem',
                            borderCollapse: 'separate'
                        }}>
                            <thead
                                style={{
                                    position: "sticky",
                                    top: 0,
                                    background: "white",
                                    zIndex: 1,
                                }}>
                                <tr>
                                    <th>#</th>
                                    <th>{t("shared.tool_name", "Name")}</th>
                                    <th>{t("business.tools.tool_id", "Tool ID")}</th>
                                    <th>{t("shared.serial_number", "Serial Number")}</th>
                                    <th>{t("shared.status", "Status")}</th>
                                    <th>{t("shared.condition", "Condition")}</th>
                                    <th>{t("shared.location", "Location")}</th>
                                    <th>{t("shared.actions", "Actions")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence>
                                {tools?.map((tool, ind) => (
                                    <motion.tr
                                        key={tool?.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <th scope="row">{ind+1}</th>
                                        <td
                                            className='text-primary'
                                            role='button'
                                            onClick={() => {
                                                setSelectedTool(tool);
                                                setDetailsModal(true);
                                            }}
                                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                        >
                                            {tool.name || t("shared.n_a", 'N/A')}
                                        </td>
                                        <td>{tool.toolNumber || ''}</td>
                                        <td>{tool.serialNumber || ''}</td>
                                        <td>{getStatusBadge(tool.status)}</td>
                                        <td>
                                            {tool.condition && (
                                                <Badge color={
                                                    tool.condition.toUpperCase() === 'EXCELLENT' ? 'success' :
                                                    tool.condition.toUpperCase() === 'GOOD' ? 'primary' :
                                                    tool.condition.toUpperCase() === 'FAIR' ? 'warning' : 'danger'
                                                } className="badge-soft" style={{ fontWeight: '400' }}>
                                                    {t(`shared.condition_${tool.condition.toLowerCase()}`, tool.condition)}
                                                </Badge>
                                            )}
                                        </td>
                                        <td
                                            className='text-primary'
                                            role='button'
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => tool.location && handleLocationClick(tool.location)}
                                        >
                                            {tool.location || ''}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                {tool.status === 'AVAILABLE' && (
                                                    <PermissionGuard permission="tool:update">
                                                        <span
                                                            className='text-warning'
                                                            role='button'
                                                            title={t('business.tools.check_out', 'Check Out')}
                                                            onClick={() => {
                                                                setCheckoutToolId(tool.id);
                                                                setCheckoutModal(true);
                                                            }}
                                                        >
                                                            <FeatherIcon icon="log-out" size={16} />
                                                        </span>
                                                    </PermissionGuard>
                                                )}
                                                {tool.status === 'CHECKED_OUT' && (
                                                    <PermissionGuard permission="tool:update">
                                                        <span
                                                            className='text-success'
                                                            role='button'
                                                            title={t('business.tools.check_in', 'Check In')}
                                                            onClick={() => handleCheckinFromList(tool.id)}
                                                        >
                                                            <FeatherIcon icon="log-in" size={16} />
                                                        </span>
                                                    </PermissionGuard>
                                                )}
                                                {canDeleteTool(tool) && (
                                                    <PermissionGuard permission="tool:delete">
                                                        <span className='text-danger' role='button' onClick={() => {
                                                            setSelectedEntry(tool);
                                                            setDeleteModal(true);
                                                        }}>
                                                            <FeatherIcon icon="trash-2" size={16} />
                                                        </span>
                                                    </PermissionGuard>
                                                )}
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                        {loading && (
                            <div className="text-center py-3">
                                <Spinner color="primary" />
                            </div>
                        )}
                    </div>
                    {tools.length === 0 && !loading && (
                        <div className="text-center py-4">
                            <p className="text-muted">{t("business.tools.no_tools_found", "No tools found")}</p>
                        </div>
                    )}
                    {hasMore && tools.length > 0 && !loading && (
                        <div className="text-center py-3">
                            <button
                                className="btn btn-outline-primary"
                                onClick={handleLoadMore}
                                disabled={loading}
                            >
                                {t("shared.load", "Load More")}
                            </button>
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Add/Edit Modal */}
            <Modal size="lg" isOpen={modal_large} toggle={() => tog_large()}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-header text-white">
                        <h5 className="modal-title fs-4">
                            {selectedEntry?.id ? t("shared.button_edit_tool", 'Edit Tool') : t("business.tools.add_tool", 'Add Tool')}
                        </h5>
                        <button type="button" className="btn-close btn-close-white" onClick={handleModalClose} aria-label={t("shared.close", "Close")}></button>
                    </div>

                    <div className="modal-body p-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        <form>
                            {apiError && <div className="alert alert-danger">{apiError}</div>}
                            {notification.message && (
                                <div className={`alert ${notification.type === 'success' ? 'alert-success' : notification.type === 'warning' ? 'alert-warning' : 'alert-danger'} d-flex align-items-center`}>
                                    <FeatherIcon icon={notification.type === 'success' ? 'check-circle' : notification.type === 'warning' ? 'alert-triangle' : 'x-circle'} size={18} className="me-2" />
                                    {notification.message}
                                </div>
                            )}

                            {/* 1. Tool Name (full width, required) */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="name" className="form-label fw-medium">{t("shared.tool_name", "Tool Name")} <span className="text-danger">*</span></label>
                                        <input type="text" id="name" className={`form-control ${errors.name ? 'is-invalid' : ''}`} value={selectedEntry?.name || ''} onChange={(e) => setSelectedEntry({...selectedEntry, name: e.target.value})} />
                                        {errors.name && <small className="text-danger">{errors.name}</small>}
                                    </div>
                                </Col>
                            </Row>

                            {/* 2. Tool ID */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="toolNumber" className="form-label fw-medium">{t("business.tools.tool_id", "Tool ID")}</label>
                                        <input type="text" id="toolNumber" className="form-control" placeholder={t("business.tools.tool_id_placeholder", "Enter tool ID (auto-generated if empty)")} value={selectedEntry?.toolNumber || ''} onChange={(e) => setSelectedEntry({...selectedEntry, toolNumber: e.target.value})} />
                                    </div>
                                </Col>
                            </Row>

                            {/* 3. Serial Number */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="serialNumber" className="form-label fw-medium">{t("shared.serial_number", "Serial Number")}</label>
                                        <input type="text" id="serialNumber" className="form-control" value={selectedEntry?.serialNumber || ''} onChange={(e) => setSelectedEntry({...selectedEntry, serialNumber: e.target.value})} />
                                    </div>
                                </Col>
                            </Row>

                            {/* 4. Location (react-select, required) */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="locationSelect" className="form-label fw-medium">{t("shared.location", "Location")} <span className="text-danger">*</span></label>
                                        <Select
                                            id="locationSelect"
                                            styles={selectStyles(!!errors.location)}
                                            options={flatLocations.map(loc => ({ value: loc.id, label: loc.name, path: loc.path }))}
                                            formatOptionLabel={(option) => (
                                                <div>
                                                    <div className="fw-medium">{option.label}</div>
                                                    {option.path && option.path !== option.label && (
                                                        <div className="text-muted small">{option.path}</div>
                                                    )}
                                                </div>
                                            )}
                                            filterOption={(option, inputValue) => {
                                                if (!inputValue) return true;
                                                const search = inputValue.toLowerCase();
                                                return option.data.label.toLowerCase().includes(search) ||
                                                    option.data.path.toLowerCase().includes(search);
                                            }}
                                            value={
                                                selectedEntry?.locationId
                                                    ? (() => {
                                                        const found = flatLocations.find(loc => loc.id == selectedEntry.locationId);
                                                        return found ? { value: found.id, label: found.name, path: found.path } : null;
                                                    })()
                                                    : null
                                            }
                                            onChange={(option) => {
                                                const locId = option ? option.value : '';
                                                const locObj = flatLocations.find(l => l.id === locId);
                                                setSelectedEntry({...selectedEntry, locationId: locId, location: locObj?.name || ''});
                                                if (errors.location) setErrors(prev => ({...prev, location: ''}));
                                            }}
                                            isClearable
                                            isSearchable
                                            placeholder={t("shared.search_and_select_location", "Search and select location...")}
                                            classNamePrefix="react-select"
                                        />
                                        {errors.location && <small className="text-danger">{errors.location}</small>}
                                    </div>
                                </Col>
                            </Row>

                            {/* 5. Condition (react-select, required) */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="condition" className="form-label fw-medium">{t("shared.condition", "Condition")} <span className="text-danger">*</span></label>
                                        <Select
                                            id="condition"
                                            styles={selectStyles(!!errors.condition)}
                                            options={conditions.map(c => ({ value: c, label: t(`shared.condition_${c.toLowerCase()}`, c) }))}
                                            value={selectedEntry?.condition ? { value: selectedEntry.condition, label: t(`shared.condition_${selectedEntry.condition.toLowerCase()}`, selectedEntry.condition) } : null}
                                            onChange={(option) => {
                                                setSelectedEntry({...selectedEntry, condition: option ? option.value : ''});
                                                if (errors.condition) setErrors(prev => ({...prev, condition: ''}));
                                            }}
                                            isClearable
                                            isSearchable
                                            placeholder={t("shared.select_condition", "Select Condition")}
                                            classNamePrefix="react-select"
                                        />
                                        {errors.condition && <small className="text-danger">{errors.condition}</small>}
                                    </div>
                                </Col>
                            </Row>

                            {/* 6. Status */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="toolStatus" className="form-label fw-medium">{t("shared.status", "Status")}</label>
                                        <Select
                                            id="toolStatus"
                                            styles={selectStyles(false)}
                                            classNamePrefix="react-select"
                                            options={selectedEntry?.status === 'CHECKED_OUT'
                                                ? [{ value: 'CHECKED_OUT', label: t("business.tools.status_checked_out", "Checked Out") }]
                                                : toolStatuses.map(s => ({ value: s.value, label: s.label }))
                                            }
                                            value={selectedEntry?.status
                                                ? {
                                                    value: selectedEntry.status,
                                                    label: statusBadgeMap[selectedEntry.status]?.label || selectedEntry.status
                                                }
                                                : { value: 'AVAILABLE', label: t("business.tools.status_available", "Available") }
                                            }
                                            onChange={(option) => setSelectedEntry({...selectedEntry, status: option ? option.value : 'AVAILABLE'})}
                                            isDisabled={selectedEntry?.status === 'CHECKED_OUT'}
                                        />
                                        {selectedEntry?.status === 'CHECKED_OUT' && (<small className="text-muted">{t("business.tools.checked_out_system_managed", "Status is system-managed while tool is checked out")}</small>)}
                                    </div>
                                </Col>
                            </Row>

                            {/* 7. Category */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="category" className="form-label fw-medium">{t("business.tools.category", "Category")}</label>
                                        <Select
                                            id="category"
                                            styles={selectStyles(false)}
                                            classNamePrefix="react-select"
                                            options={toolCategories}
                                            value={toolCategories.find(c => c.value === selectedEntry?.category) || null}
                                            onChange={(option) => setSelectedEntry({...selectedEntry, category: option ? option.value : ''})}
                                            isClearable
                                            isSearchable
                                            placeholder={t("business.tools.select_category", "Select Category")}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            {/* 8. Manufacturer */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="toolManufacturer" className="form-label fw-medium">{t("business.tools.manufacturer", "Manufacturer")}</label>
                                        <input type="text" id="toolManufacturer" className="form-control" value={selectedEntry?.manufacturer || ''} onChange={(e) => setSelectedEntry({...selectedEntry, manufacturer: e.target.value})} />
                                    </div>
                                </Col>
                            </Row>

                            {/* 9. Model */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="toolModel" className="form-label fw-medium">{t("business.tools.model", "Model")}</label>
                                        <input type="text" id="toolModel" className="form-control" value={selectedEntry?.model || ''} onChange={(e) => setSelectedEntry({...selectedEntry, model: e.target.value})} />
                                    </div>
                                </Col>
                            </Row>

                            {/* 10. Description */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="description" className="form-label fw-medium">{t("shared.description", "Description")}</label>
                                        <textarea id="description" className="form-control" rows="2" value={selectedEntry?.description || ''} onChange={(e) => setSelectedEntry({...selectedEntry, description: e.target.value})} />
                                    </div>
                                </Col>
                            </Row>

                            {/* 11. Vendor */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="vendorId" className="form-label fw-medium">{t("business.tools.vendor", "Vendor")}</label>
                                        <Select
                                            id="vendorId"
                                            styles={selectStyles(false)}
                                            classNamePrefix="react-select"
                                            options={vendors?.map(v => ({ value: v.id, label: v.vendorName })) || []}
                                            value={
                                                selectedEntry?.vendorId
                                                    ? vendors?.find(v => v.id == selectedEntry.vendorId)
                                                        ? { value: selectedEntry.vendorId, label: vendors.find(v => v.id == selectedEntry.vendorId)?.vendorName }
                                                        : null
                                                    : null
                                            }
                                            onChange={(option) => setSelectedEntry({...selectedEntry, vendorId: option ? option.value : null})}
                                            isClearable
                                            isSearchable
                                            placeholder={t("business.tools.select_vendor", "Search and select vendor...")}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            {/* 12. Cost & Currency */}
                            <Row className="mb-4">
                                <Col md={6}>
                                    <div className="form-group">
                                        <label htmlFor="cost" className="form-label fw-medium">{t("shared.cost", "Cost")}</label>
                                        <input type="number" id="cost" step="0.01" className="form-control" value={selectedEntry?.cost || ''} onChange={(e) => setSelectedEntry({...selectedEntry, cost: parseFloat(e.target.value) || 0})} placeholder={t("business.tools.enter_cost", "Enter cost")} />
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="form-group">
                                        <label htmlFor="currency" className="form-label fw-medium">{t("shared.currency", "Currency")}</label>
                                        {(() => {
                                            // Currency is org-level. Pick from the tool's parent org on edit, else the
                                            // active org's currency on create. Disabled to prevent per-tool overrides.
                                            let activeOrgCurrency = 'USD';
                                            try {
                                                const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
                                                activeOrgCurrency = authUser?.activeOrganizationCurrency || 'USD';
                                            } catch { /* fall through */ }
                                            const orgCurrency = selectedEntry?.organization?.currency || activeOrgCurrency;
                                            return (
                                                <Select
                                                    id="currency"
                                                    options={Object.entries(currencies(t)).map(([code, label]) => ({
                                                        value: code,
                                                        label,
                                                    }))}
                                                    value={{ value: orgCurrency, label: currencies(t)[orgCurrency] || orgCurrency }}
                                                    isDisabled
                                                    classNamePrefix="react-select"
                                                    styles={selectStyles(false)}
                                                />
                                            );
                                        })()}
                                        <small className="text-muted">
                                            {t(
                                                "shared.currency_org_locked_hint",
                                                "Set under Organization Settings — applies to every asset, part, and tool in the organization."
                                            )}
                                        </small>
                                    </div>
                                </Col>
                            </Row>

                            {/* 13. Purchase Date */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="purchaseDate" className="form-label fw-medium">{t("business.tools.purchase_date", "Purchase Date")}</label>
                                        <DatePicker selected={selectedEntry?.purchaseDate ? new Date(selectedEntry.purchaseDate) : null} onChange={(date) => setSelectedEntry({...selectedEntry, purchaseDate: date ? date.toISOString().split('T')[0] : ''})} dateFormat="dd/MM/yyyy" className="form-control" placeholderText={t('shared.select_date', 'Select date')} isClearable showMonthDropdown showYearDropdown dropdownMode="select" />
                                    </div>
                                </Col>
                            </Row>

                            {/* 14. Image (asset-style dashed upload) */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label className="form-label fw-medium">{t("business.tools.image", "Image")}</label>
                                        <div
                                            className="border rounded p-4 text-center"
                                            style={{ borderStyle: 'dashed', borderWidth: '2px', borderColor: '#dee2e6', backgroundColor: '#fafbfc' }}
                                        >
                                            <input
                                                type="file"
                                                id="toolImageUploadInput"
                                                className="d-none"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                            />
                                            <label
                                                htmlFor="toolImageUploadInput"
                                                className="btn btn-outline-primary mb-2"
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <FeatherIcon icon="upload" size={16} className="me-2" />
                                                {t("shared.upload_images", "Upload Image")}
                                            </label>
                                            <p className="text-muted small mb-0">
                                                {t("shared.click_to_select_images", "Click to select an image to upload")}
                                            </p>
                                        </div>
                                        {selectedEntry?.imageUrl && (
                                            <div className="mt-3">
                                                <small className="text-muted d-block mb-2">{t("business.tools.current_image", "Current Image:")}</small>
                                                <div className="position-relative d-inline-block">
                                                    <img
                                                        src={selectedEntry.imageUrl}
                                                        alt="Tool"
                                                        className="rounded border"
                                                        style={{ width: '80px', height: '80px', objectFit: 'cover', cursor: 'pointer' }}
                                                        onClick={() => window.open(selectedEntry.imageUrl, '_blank')}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm position-absolute"
                                                        style={{ top: '-8px', right: '-8px', padding: '2px 6px', borderRadius: '50%' }}
                                                        onClick={() => setSelectedEntry({...selectedEntry, imageUrl: null})}
                                                    >
                                                        <FeatherIcon icon="x" size={12} />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            {/* 14. Documents */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label className="form-label fw-medium">{t("shared.documents", "Documents")}</label>
                                        <div
                                            className="border rounded p-4 text-center"
                                            style={{ borderStyle: 'dashed', borderWidth: '2px', borderColor: '#dee2e6', backgroundColor: '#fafbfc' }}
                                        >
                                            <input
                                                type="file"
                                                id="toolDocUploadInput"
                                                className="d-none"
                                                accept=".pdf,.doc,.docx,.xls,.xlsx"
                                                multiple
                                                onChange={handleDocumentChange}
                                                disabled={docUploading}
                                            />
                                            <label
                                                htmlFor="toolDocUploadInput"
                                                className={`btn btn-outline-secondary mb-2 ${docUploading ? 'disabled' : ''}`}
                                                style={{ cursor: docUploading ? 'not-allowed' : 'pointer' }}
                                            >
                                                {docUploading ? (
                                                    <><Spinner size="sm" className="me-2" />{t("shared.uploading", "Uploading...")}</>
                                                ) : (
                                                    <><FeatherIcon icon="paperclip" size={16} className="me-2" />{t("business.tools.attach_documents", "Attach Documents")}</>
                                                )}
                                            </label>
                                            <p className="text-muted small mb-0">
                                                {docUploading ? t("business.tools.uploading_documents_wait", 'Please wait while documents are being uploaded...') : t("business.tools.pdf_word_excel", 'PDF, Word, Excel files (max 50MB)')}
                                            </p>
                                        </div>
                                        {/* Display existing/uploaded documents */}
                                        {selectedEntry?.documentUrls?.length > 0 && (
                                            <div className="mt-3">
                                                <small className="text-muted d-block mb-2">{t("shared.current_documents", "Current Documents:")}</small>
                                                <div className="d-flex flex-column gap-2">
                                                    {selectedEntry.documentUrls.map((url, index) => (
                                                        <div key={index} className="d-flex align-items-center p-2 bg-light rounded">
                                                            <div className="d-flex align-items-center justify-content-center me-2"
                                                                 style={{ width: '32px', height: '32px', backgroundColor: '#e9ecef', borderRadius: '4px' }}>
                                                                <FeatherIcon icon={getDocumentIcon(url)} size={16} className="text-primary" />
                                                            </div>
                                                            <div className="flex-grow-1 overflow-hidden">
                                                                <p className="mb-0 text-truncate small fw-medium">{getFileNameFromUrl(url)}</p>
                                                                <small className="text-muted">{url.split('.').pop()?.toUpperCase()}</small>
                                                            </div>
                                                            <div className="d-flex gap-1">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-primary btn-sm"
                                                                    onClick={() => window.open(url, '_blank')}
                                                                >
                                                                    <FeatherIcon icon="download" size={12} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-danger btn-sm"
                                                                    onClick={() => handleDeleteDocument(index)}
                                                                >
                                                                    <FeatherIcon icon="trash-2" size={12} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </form>
                    </div>

                    <div className="modal-footer bg-light border-top-0">
                        <button type="button" className="btn btn-outline-secondary" onClick={handleModalClose}>
                            {t("shared.cancel", "Cancel")}
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleUpdate} disabled={loading}>
                            {loading ? (
                                <><Spinner size="sm" className="me-2" />{selectedEntry?.id ? t("shared.updating", "Updating...") : t("shared.creating", "Creating...")}</>
                            ) : (
                                selectedEntry?.id ? t("shared.update", "Update") : t("business.tools.add_tool", "Add Tool")
                            )}
                        </button>
                    </div>
                </motion.div>
            </Modal>

            {/* Delete Modal */}
            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
                <div className="modal-header">
                    <h5 className="modal-title">{t("shared.confirm_delete", "Confirm Delete")}</h5>
                    <button type="button" className="btn-close" onClick={() => setDeleteModal(false)} aria-label={t("shared.close", "Close")}></button>
                </div>
                <div className="modal-body">
                    <p>{t("business.tools.delete_confirmation", "Are you sure you want to delete this tool? This action cannot be undone.")}</p>
                </div>
                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setDeleteModal(false)}>
                        {t("shared.cancel", "Cancel")}
                    </button>
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleDelete}
                    >
                        {t("shared.delete", "Delete")}
                    </button>
                </div>
            </Modal>

            <FileModal
                title={t("shared.tools", 'Tools')}
                isOpen={fileModal}
                toggle={handleClose}
                data={tools}
                onImport={handleImport}
                onExport={handleExport}
                error={apiError}
                loading={loading}
                importLabel={t("business.tools.import_tools", "Import Tools from Excel")}
                exportLabel={t("business.tools.export_tools", "Export Tools to Excel")}
            />

            {/* Tool Details Modal */}
            <ToolsModal
                isOpen={detailsModal}
                toggle={() => setDetailsModal(false)}
                toolData={selectedTool}
                loading={false}
                onEdit={tog_large}
                onCheckout={handleCheckoutFromModal}
                onCheckin={handleCheckinFromModal}
                onWorkOrderClick={(ticketNumber) => {
                    setDetailsModal(false);
                    setWoTicketModal({ isOpen: true, ticketNumber });
                }}
            />

            {/* Checkout Modal */}
            <ToolCheckoutModal
                isOpen={checkoutModal}
                toggle={() => { setCheckoutModal(false); setCheckoutToolId(null); }}
                toolId={checkoutToolId}
                onSuccess={handleCheckoutSuccess}
            />

            {/* Checkin Modal */}
            <ToolCheckinModal
                isOpen={checkinModal}
                toggle={() => { setCheckinModal(false); setCheckinCheckoutId(null); }}
                checkoutId={checkinCheckoutId}
                onSuccess={handleCheckinSuccess}
            />

            {/* Work Order Details Modal */}
            <TicketDetailsModal
                isOpen={woTicketModal.isOpen}
                toggle={() => setWoTicketModal({ isOpen: false, ticketNumber: null })}
                ticketNumber={woTicketModal.ticketNumber}
                formatCreatedDate={formatDateDisplay}
            />

            {/* Filter Modal */}
            <Modal
                isOpen={filterModal}
                toggle={() => setFilterModal(false)}
                centered
                className="fade-scale-modal"
                backdropClassName="modal-backdrop-blur"
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-header text-white">
                        <h5 className="modal-title fs-4">{t("business.tools.filter_tools", "Filter Tools")}</h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={() => setFilterModal(false)}
                            aria-label={t("shared.close", "Close")}
                        ></button>
                    </div>

                    <div className="modal-body p-4">
                        <Row className="mb-4 g-3">
                            <Col md={12}>
                                <div className="form-group">
                                    <label htmlFor="toolFilterStatus" className="form-label fw-medium">
                                        {t("shared.status", "Status")}
                                    </label>
                                    <select
                                        id="toolFilterStatus"
                                        className="form-select"
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="">{t("business.tools.all_statuses", "All Statuses")}</option>
                                        <option value="AVAILABLE">{t("business.tools.status_available", "Available")}</option>
                                        <option value="CHECKED_OUT">{t("business.tools.status_checked_out", "Checked Out")}</option>
                                        <option value="UNDER_MAINTENANCE">{t("business.tools.status_under_maintenance", "Under Maintenance")}</option>
                                        <option value="OUT_OF_SERVICE">{t("business.tools.status_out_of_service", "Out of Service")}</option>
                                    </select>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="modal-footer bg-light border-top-0">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => {
                                setStatusFilter('');
                                setCount(10);
                                fetchToolsWithParams('', searchTerm);
                                setFilterModal(false);
                            }}
                        >
                            {t("shared.clear_all", "Clear All")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                setCount(10);
                                fetchToolsWithParams(statusFilter, searchTerm);
                                setFilterModal(false);
                            }}
                        >
                            {t("shared.apply_filters", "Apply Filters")}
                        </button>
                    </div>
                </motion.div>
            </Modal>

            {/* Location Details Modal */}
            <LocationModal
                isOpen={locationDetailsModal.isOpen}
                toggle={() => setLocationDetailsModal(prev => ({ ...prev, isOpen: false }))}
                title={locationDetailsModal.title}
                mainData={locationDetailsModal.mainData}
                relatedData={locationDetailsModal.relatedData}
                loading={locationDetailsModal.loading}
                locationData={locationDetailsModal.locationData}
                onLocationUpdated={(updatedLocation) => {
                    setLocationDetailsModal(prev => ({ ...prev, locationData: updatedLocation }));
                }}
            />
        </div>
    );
};

export default Tools;
